import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const Navbar = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => {
    setIsOpen(false);
    setActiveDropdown(null);
  };

  const menuItems = [
    {
      title: "หน้าแรก",
      path: "/",
      icon: "fas fa-home",
    },
    {
      title: "เกี่ยวกับหน่วยงาน",
      icon: "fas fa-building",
      dropdown: [
        { title: "ประวัติความเป็นมา", path: "/about/history" },
        { title: "ปรัชญา วิสัยทัศน์ พันธกิจ", path: "/about/philosophy" },
        { title: "โครงสร้างการบริหาร", path: "/about/structure" },
        { title: "บุคลากร", path: "/about/staff" },
        { title: "กฎหมาย / ระเบียบ", path: "/about/law" },
      ],
    },
    {
      title: "บริการวิชาการ",
      icon: "fas fa-hand-holding-heart",
      dropdown: [
        { title: "ข่าวประชาสัมพันธ์", path: "/news" },
        { title: "พื้นที่บริการวิชาการ", path: "/projects" },
        { title: "คลังปัญญา", path: "/knowledge" },
        {
          title: "Market SSKRU",
          path: "https://market.sskru.ac.th/",
          target: "_blank",
        },
      ],
    },
    {
      title: "รายงาน",
      path: "/report",
      icon: "fas fa-chart-bar",
    },
    {
      title: "ติดต่อเรา",
      path: "/contact",
      icon: "fas fa-envelope",
    },
  ];

  if (isAdmin) {
    menuItems.push({
      title: "จัดการระบบ",
      icon: "fas fa-cog",
      dropdown: [
        { title: "จัดการโครงการ", path: "/admin/projects" },
        { title: "จัดการองค์ความรู้", path: "/admin/knowledge" },
        { title: "จัดการข่าว", path: "/admin/news" },
        { title: "จัดการผู้ใช้", path: "/admin/users" },
      ],
    });
  }

  return (
    <nav className="bg-primary-500 sticky top-0 z-50 shadow-lg">
      <div className="container-custom">
        {/* Mobile Toggle */}
        <div className="lg:hidden flex items-center justify-between py-3">
          <button
            onClick={toggleMenu}
            className="text-white p-2 hover:bg-primary-600 rounded-lg transition-colors"
          >
            <i className={`fas fa-${isOpen ? "times" : "bars"} text-xl`}></i>
          </button>
          {!isAuthenticated && (
            <Link
              to="/login"
              className="bg-secondary-500 text-dark-500 px-4 py-2 rounded-lg font-semibold"
              onClick={closeMenu}
            >
              เข้าสู่ระบบ
            </Link>
          )}
        </div>

        {/* Navigation Menu */}
        <div className={`${isOpen ? "block" : "hidden"} lg:block`}>
          <ul className="flex flex-col lg:flex-row lg:items-center lg:justify-center gap-2 lg:gap-6 py-3">
            {menuItems.map((item, index) => (
              <li key={index} className="relative group">
                {item.dropdown ? (
                  <>
                    <span
                      className="flex items-center gap-2 text-white hover:text-secondary-500 transition-colors py-2 px-3 cursor-pointer"
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === index ? null : index,
                        )
                      }
                    >
                      <i className={item.icon}></i>
                      <span>{item.title}</span>
                      <i className="fas fa-chevron-down text-sm ml-1"></i>
                    </span>
                    <ul
                      className={`
                      lg:absolute lg:left-0 lg:mt-2 w-56 bg-white rounded-lg shadow-xl 
                      ${activeDropdown === index || "lg:group-hover:block"} 
                      ${activeDropdown === index ? "block" : "hidden"} 
                      lg:block lg:opacity-0 lg:invisible lg:group-hover:opacity-100 lg:group-hover:visible 
                      transition-all duration-300 z-50
                    `}
                    >
                      {item.dropdown.map((dropItem, dropIndex) => (
                        <li key={dropIndex}>
                          {dropItem.target === "_blank" ? (
                            <a
                              href={dropItem.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-primary-500"
                              onClick={closeMenu}
                            >
                              {dropItem.title}
                            </a>
                          ) : (
                            <Link
                              to={dropItem.path}
                              className="block px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-primary-500"
                              onClick={closeMenu}
                            >
                              {dropItem.title}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <NavLink
                    to={item.path}
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `flex items-center gap-2 text-white hover:text-secondary-500 transition-colors py-2 px-3 
                      ${isActive ? "text-secondary-500 border-b-2 border-secondary-500" : ""}`
                    }
                  >
                    <i className={item.icon}></i>
                    <span>{item.title}</span>
                  </NavLink>
                )}
              </li>
            ))}

            {/* Dashboard Link - สำหรับ user ที่ login แล้ว */}
            {isAuthenticated && (
              <li>
                <NavLink
                  to="/dashboard"
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    `flex items-center gap-2 text-white hover:text-secondary-500 transition-colors py-2 px-3 
                    ${isActive ? "text-secondary-500 border-b-2 border-secondary-500" : ""}`
                  }
                >
                  <i className="fas fa-tachometer-alt"></i>
                  <span>แดชบอร์ด</span>
                </NavLink>
              </li>
            )}

            {/* Desktop Login Button */}
            {!isAuthenticated && (
              <li className="hidden lg:block">
                <Link
                  to="/login"
                  className="bg-secondary-500 text-dark-500 px-5 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-all hover:shadow-lg hover:-translate-y-1 inline-flex items-center gap-2"
                  onClick={closeMenu}
                >
                  <i className="fas fa-sign-in-alt"></i>
                  เข้าสู่ระบบ
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
